import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandCurrencySuffix, getBrandLocale, resolveLiveVatRate } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, Search, Plus, Edit, Trash2, DollarSign, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Users, Tag, TrendingUp, Columns3 } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import FacturationFormModalTabs from './components/FacturationFormModalTabs';
import { articleFormLabelClass, ERP_MODAL_FORM_CLASS } from './components/articleFormFieldClasses';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { QawaemTableStickyActionsTd, mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildServiceStatusBadgeOptions,
  buildServiceStatusColumnFilterOptions,
  buildServiceStatusSelectOptions,
  getCatalogStatusLabel,
} from '../articles/articleCatalogStatusOptions';
import {
  CATALOG_UNITS_REFRESH_EVENT,
  fetchCatalogUnits,
  buildUnitSelectOptionsFromRecords,
  resolveUnitLabelFromOptions,
  collectOrphanUnitValues,
  defaultUnitFromOptions,
  resolveCatalogUnitLabelArabic,
  normalizeServiceUnitCode,
  resolveServiceUnitLabel,
} from '../articles/catalogUnitOptions';
import {
  buildCategorySelectOptions,
  CATALOG_CATEGORIES_REFRESH_EVENT,
  filterCategoriesByType,
  findCategoryByRef,
  normalizeCategoriesResponse,
} from '../articles/articleCatalogCategories';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { dispatchCatalogProductsRefresh } from '../articles/catalogProductOptions';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

registerLocale('fr', fr);
registerLocale('ar', arLocale);

// Inline edit cell for Services - MUST stay outside Services
function ServiceEditableCell({
  recordId, field, value, className = 'text-white font-semibold',
  isEditing, editValue, onEditValueChange, onStartEdit, onSave, onCancel,
  displayValue,
  readOnly = true,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch {}
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return (
      <span className={className}>
        {(displayValue ?? value) != null && String(displayValue ?? value).trim() !== ''
          ? (displayValue ?? value)
          : '-'}
      </span>
    );
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef} type="text" value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) { skipBlurSaveRef.current = false; return; }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlurSaveRef.current = true; onSave(recordId, field, e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }} autoComplete="off" spellCheck={false}
      />
    );
  }

  return (
    <span role="button" tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStartEdit(recordId, field, value); } }}
      className={`${className} cursor-pointer`} title={t('modules.article.pages.services.click_to_edit')}
    >{(displayValue ?? value) || '-'}</span>
  );
}

const Services = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  // Close dropdowns on click outside (unit + VAT portals only; category/status via CustomSelect)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnUnitButton = event.target.closest('[data-services-unit-dropdown-button]');
      const isClickInUnitDropdown = event.target.closest('[data-services-unit-dropdown]');
      if (!isClickOnUnitButton && !isClickInUnitDropdown) {
        setShowUnitDropdown(false);
      }

      const isClickOnTVAButton = event.target.closest('[data-services-tva-dropdown-button]');
      const isClickInTVADropdown = event.target.closest('[data-services-tva-dropdown]');
      if (!isClickOnTVAButton && !isClickInTVADropdown) {
        setShowTVADropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const { settings } = useSettings();
  const articlesConfig = useArticlesConfig();
  const liveVatRate = useMemo(
    () => resolveLiveVatRate(settings, articlesConfig),
    [settings?.taxes?.tva, settings?.facturation?.defaultTaxRate, articlesConfig?.defaultTaxRate],
  );
  const liveVatRateStr = String(liveVatRate);
  const [searchTerm, setSearchTerm] = useState('');
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogServiceUnits, setCatalogServiceUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Inline editing states
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  const [serviceFormTab, setServiceFormTab] = useState('basic');
  
  const [formData, setFormData] = useState({ 
    service_number: '',
    name_en: '',
    name_ar: '',
    name: '', 
    description: '',
    category: '', 
    unit: '', 
    period: '',
    price: '', 
    duration: '',
    status: 'active',
    taxRate: liveVatRateStr,
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showTVADropdown, setShowTVADropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_services');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);
  /** Panneau carte de la modale service — pour garder le menu Unité à l’intérieur */
  const serviceModalPanelRef = useRef(null);
  const tvaDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const categoryFilterRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = t('modules.article.pages.services.entity_label', { defaultValue: 'الخدمة' });
  const entityLabelPlural = t('modules.article.pages.services.entity_label_plural', { defaultValue: 'خدمة' });

  const loadServices = async () => {
    try {
      const response = await localApiAdapter.get('/services');
      const rows = response?.data || response || [];
      setServices(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]);
    }
  };

  const loadCategories = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/categories?type=service');
      const serviceCategories = filterCategoriesByType(normalizeCategoriesResponse(response), 'service');
      setCategories(serviceCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  }, []);

  const loadCatalogServiceUnits = useCallback(async () => {
    const rows = await fetchCatalogUnits('service');
    setCatalogServiceUnits(rows);
  }, []);

  useEffect(() => { loadServices(); loadCategories(); loadCatalogServiceUnits(); }, [loadCategories, loadCatalogServiceUnits]);

  useEffect(() => {
    if (!showModal) return;
    void loadCategories();
    void loadCatalogServiceUnits();
  }, [showModal, loadCategories, loadCatalogServiceUnits]);

  useEffect(() => {
    const handleCategoriesRefresh = () => { void loadCategories(); };
    window.addEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, handleCategoriesRefresh);
    window.addEventListener('refresh-all-data', handleCategoriesRefresh);
    return () => {
      window.removeEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, handleCategoriesRefresh);
      window.removeEventListener('refresh-all-data', handleCategoriesRefresh);
    };
  }, [loadCategories]);

  useEffect(() => {
    const handleUnitsRefresh = () => { void loadCatalogServiceUnits(); };
    window.addEventListener(CATALOG_UNITS_REFRESH_EVENT, handleUnitsRefresh);
    window.addEventListener('refresh-all-data', handleUnitsRefresh);
    return () => {
      window.removeEventListener(CATALOG_UNITS_REFRESH_EVENT, handleUnitsRefresh);
      window.removeEventListener('refresh-all-data', handleUnitsRefresh);
    };
  }, [loadCatalogServiceUnits]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (categoryFilterRef.current && !categoryFilterRef.current.contains(event.target)) setIsCategoryFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stats = {
    total: services.length,
    active: services.filter(s => s.status === 'active').length,
    inactive: services.filter(s => s.status === 'inactive').length,
    categories: [...new Set(services.map(s => s.category))].filter(Boolean).length,
    totalRevenue: services.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0)
  };

  const mainTabs = useMemo(() => [
    { id: 'all_services', name: t('modules.article.pages.services.tabs.general'), icon: LayoutGrid },
    { id: 'par_categorie', name: t('modules.article.pages.services.tabs.by_category'), icon: Tag },
    { id: 'par_prix', name: t('modules.article.pages.services.tabs.by_price'), icon: DollarSign }
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: t('modules.article.pages.services.filters.all'), icon: Layers },
    { id: 'active', name: t('modules.article.pages.services.filters.active'), icon: CheckCircle },
    { id: 'inactive', name: t('modules.article.pages.services.filters.inactive'), icon: XCircle },
    { id: 'popular', name: t('modules.article.pages.services.filters.popular'), icon: TrendingUp }
  ], [t]);

  const servicesDistribution = useMemo(() => ({
    all_services: {
      title: t('modules.article.pages.services.distribution.overview_title'),
      data: [
        { filterId: 'active', name: t('modules.article.pages.services.distribution.active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterId: 'inactive', name: t('modules.article.pages.services.distribution.inactive'), count: stats.inactive, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterId: 'categories', name: t('modules.article.pages.services.distribution.categories'), count: stats.categories, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    },
    par_categorie: {
      title: t('modules.article.pages.services.distribution.by_category_title'),
      data: categories.slice(0, 3).map(cat => ({
        filterId: `cat:${cat.id}`,
        name: cat.name,
        count: services.filter(s => s.category === cat.id).length,
        color: "bg-gradient-to-r from-purple-500 to-purple-600"
      }))
    },
    par_prix: {
      title: t('modules.article.pages.services.distribution.by_price_title'),
      data: [
        { filterId: 'under_5000', name: t('modules.article.pages.services.distribution.price_under_5000'), count: services.filter(s => parseFloat(s.price) < 5000).length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterId: '5000_20000', name: t('modules.article.pages.services.distribution.price_5000_20000'), count: services.filter(s => parseFloat(s.price) >= 5000 && parseFloat(s.price) < 20000).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterId: 'over_20000', name: t('modules.article.pages.services.distribution.price_over_20000'), count: services.filter(s => parseFloat(s.price) >= 20000).length, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    }
  }), [t, stats, categories, services]);

  const distributionInfo = servicesDistribution[mainViewMode] || servicesDistribution.all_services;

  const baseFilteredServices = useMemo(
    () =>
      services.filter((s) => {
        if (dateRange.start || dateRange.end) {
          const serviceDate = new Date(s.createdAt || s.date);

          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (serviceDate < startOfDay) return false;
          }

          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (serviceDate > endOfDay) return false;
          }
        }

        if (statusFilter !== 'all' && s.status !== statusFilter) return false;
        if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;

        if (
          searchTerm &&
          !(
            s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(s.service_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(s.reference || '').toLowerCase().includes(searchTerm.toLowerCase())
          )
        ) {
          return false;
        }

        if (mainViewMode === 'all_services') {
          if (quickFilter === 'active' && s.status !== 'active') return false;
          if (quickFilter === 'inactive' && s.status !== 'inactive') return false;
          if (quickFilter === 'popular' && !s.isPopular) return false;
        }

        if (activeDistributionFilter) {
          switch (mainViewMode) {
            case 'all_services':
              if (activeDistributionFilter === 'active' && s.status !== 'active') return false;
              if (activeDistributionFilter === 'inactive' && s.status !== 'inactive') return false;
              break;
            case 'par_categorie':
              if (activeDistributionFilter.startsWith('cat:') && s.category !== activeDistributionFilter.slice(4)) return false;
              break;
            case 'par_prix': {
              const price = parseFloat(s.price) || 0;
              if (activeDistributionFilter === 'under_5000' && price >= 5000) return false;
              if (activeDistributionFilter === '5000_20000' && (price < 5000 || price >= 20000)) return false;
              if (activeDistributionFilter === 'over_20000' && price < 20000) return false;
              break;
            }
            default:
              break;
          }
        }

        return true;
      }),
    [services, dateRange, statusFilter, categoryFilter, searchTerm, mainViewMode, quickFilter, activeDistributionFilter],
  );

  const getStatusLabel = useCallback((status) => getCatalogStatusLabel(status, t), [t]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('service_number', (r) => String(r.service_number || r.reference || '')),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('category', (r) => {
        const cat = categories.find((c) => c.id === r.category);
        return cat?.name || String(r.category || '');
      }),
      exactColumnRule('unit', (r) => normalizeServiceUnitCode(r.unit, '')),
      textColumnRule('price', (r) => formatBrandMoney(r.price)),
      textColumnRule('duration', (r) => String(r.duration ?? '')),
      exactColumnRule('period', (r) => r.period || ''),
      textColumnRule('tax_rate', (r) => String(r.tax_rate ?? r.taxRate ?? '')),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [categories, t],
  );

  const filteredServices = useMemo(
    () => applyColumnFilters(baseFilteredServices, colFilters, columnFilterRules),
    [baseFilteredServices, colFilters, columnFilterRules],
  );

  const servicesFooter = useMemo(() => {
    let prixCatalogue = 0;
    let actifs = 0;
    filteredServices.forEach((s) => {
      prixCatalogue += parseFloat(s.price) || 0;
      if (s.status === 'active') actifs += 1;
    });
    return { prixCatalogue, actifs };
  }, [filteredServices]);


  const serviceCategorySelectOptions = useMemo(
    () => buildCategorySelectOptions(categories, t, { includeClear: true }),
    [categories, t],
  );

  const exportHeaders = useCallback(() => [
    { key: 'service_number', label: t('modules.article.pages.services.columns.service_number') },
    { key: 'name', label: t('modules.article.pages.services.columns.name') },
    { key: 'description', label: t('modules.article.pages.services.columns.description') },
    { key: 'unit', label: t('modules.article.pages.services.columns.unit') },
    { key: 'price', label: t('modules.article.pages.services.columns.price') },
    { key: 'category', label: t('modules.article.pages.services.columns.category') },
    { key: 'status', label: t('modules.article.pages.services.columns.status') }
  ], [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const nameEn = (formData.name_en || '').trim();
      const nameAr = (formData.name_ar || '').trim();
      if (!nameEn && !nameAr) {
        alert(t('modules.article.pages.services.alerts.name_required', { defaultValue: 'الاسم بالإنجليزية أو العربية مطلوب.' }));
        return;
      }
      const svcTrim = String(formData.service_number || '').trim();
      const service_number = svcTrim || (articlesConfig.autoGenerateServiceReference !== false ? generateServiceNumber() : '');
      const submitData = { 
        service_number,
        name: nameEn || nameAr,
        name_en: nameEn || nameAr,
        name_ar: nameAr || null,
        description: formData.description,
        category: formData.category,
        unit: normalizeServiceUnitCode(formData.unit, defaultServiceUnit || ''),
        price: parseFloat(formData.price) || 0,
        duration: formData.duration,
        period: formData.period,
        status: formData.status,
        taxRate: parseFloat(formData.taxRate) || articlesConfig.defaultTaxRate || liveVatRate,
        notes: formData.notes,
        type: 'service'
      };
      if (editingRecord) {
        await localApiAdapter.put(`/services/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/services', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, t('modules.article.pages.services.alerts.save_error', { message: error.message || t('modules.article.pages.services.alerts.unknown_error') }));
      return;
    } finally {
      resetForm();
      loadServices();
      dispatchCatalogProductsRefresh();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      service_number: record.service_number || record.reference || '',
      name_en: record.name_en || record.name || '',
      name_ar: record.name_ar || '',
      name: record.name || '',
      description: record.description || '',
      category: findCategoryByRef(categories, record.category)?.id || record.category || '',
      unit: normalizeServiceUnitCode(record.unit, defaultServiceUnit || ''),
      price: record.price?.toString() || '',
      duration: record.duration || '',
      period: record.period || '',
      status: record.status || 'active',
      taxRate: record.taxRate?.toString() || liveVatRateStr,
      notes: record.notes || ''
    });
    setServiceFormTab('basic');
    setShowModal(true);
  };

  const st = (key, options) => t(`modules.article.pages.services.${key}`, options);

  const openDeleteService = (id) => openDeleteOne(id);

  const openBulkDeleteServices = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteServices = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => localApiAdapter.delete(`/services/${id}`)));
      setSelectedRecords((prev) => prev.filter((id) => !ids.includes(id)));
      loadServices();
      dispatchCatalogProductsRefresh();
      if (ids.length > 1) {
        notifyBulkDeleted(showNotify, ids.length, entityLabelPlural);
      } else {
        notifyDeleted(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error:', error);
      notifyError(
        showNotify,
        ids.length > 1
          ? st('alerts.bulk_delete_error', { defaultValue: 'تعذّر حذف الخدمات المحددة' })
          : st('alerts.delete_error', { defaultValue: 'تعذّر حذف الخدمة' }),
      );
    }
  };

  // Export handlers
  const handleExportCSV = () => {
    const headers = exportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? services.filter(s => selectedRecords.includes(s.id))
      : filteredServices;
    
    exportToCSV(dataToExport, headers, `services_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const headers = exportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? services.filter(s => selectedRecords.includes(s.id))
      : filteredServices;
    
    await exportToExcel(dataToExport, headers, `services_${new Date().toISOString().split('T')[0]}.xlsx`, t('modules.article.pages.services.export.sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0 
        ? services.filter(s => selectedRecords.includes(s.id))
        : filteredServices;

      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: t('modules.article.pages.services.columns.service_number'), value: (s) => s.service_number },
          { label: t('modules.article.pages.services.columns.name'), value: (s) => s.name },
          { label: t('modules.article.pages.services.columns.unit'), value: (s) => resolveUnitLabel(s.unit) },
          {
            label: t('modules.article.pages.services.columns.price'),
            align: 'right',
            value: (s) => formatBrandMoney(s.price || 0),
          },
          { label: t('modules.article.pages.services.columns.category'), value: (s) => s.category },
          { label: t('modules.article.pages.services.columns.status'), value: (s) => getStatusLabel(s.status) },
        ],
        listExportTitle: t('modules.article.pages.services.export.list_title_upper'),
        clientTitle: t('modules.article.pages.services.export.list_title'),
        printDocumentTitle: t('modules.article.pages.services.export.document_title', { date: new Date().toISOString().split('T')[0] }),
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      alert(t('modules.article.pages.services.alerts.pdf_export_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRecords.length > 0 
      ? services.filter(s => selectedRecords.includes(s.id))
      : filteredServices;
    
    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `services_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportXML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? services.filter(s => selectedRecords.includes(s.id))
      : filteredServices;
    
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<services>\n';
    dataToExport.forEach(item => {
      xmlContent += '  <service>\n';
      Object.keys(item).forEach(key => {
        const value = item[key] || '';
        xmlContent += `    <${key}>${value}</${key}>\n`;
      });
      xmlContent += '  </service>\n';
    });
    xmlContent += '</services>';
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `services_${new Date().toISOString().split('T')[0]}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportHTML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? services.filter(s => selectedRecords.includes(s.id))
      : filteredServices;
    
    let htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${t('modules.article.pages.services.export.html_title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1e40af; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #3b82f6; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
  </style>
</head>
<body>
  <h1>${t('modules.article.pages.services.export.html_title')}</h1>
  <p>${t('modules.article.pages.services.export.date_label')} ${new Date().toLocaleDateString(dateLocale)}</p>
  <p>${t('modules.article.pages.services.export.total_label', { count: dataToExport.length })}</p>
  <table>
    <thead>
      <tr>
        <th>${t('modules.article.pages.services.columns.service_number')}</th>
        <th>${t('modules.article.pages.services.columns.name')}</th>
        <th>${t('modules.article.pages.services.columns.unit')}</th>
        <th>${t('modules.article.pages.services.columns.price')}</th>
        <th>${t('modules.article.pages.services.columns.category')}</th>
        <th>${t('modules.article.pages.services.columns.status')}</th>
      </tr>
    </thead>
    <tbody>`;
    
    dataToExport.forEach(s => {
      htmlContent += `
      <tr>
        <td>${s.service_number || ''}</td>
        <td>${s.name || ''}</td>
        <td>${resolveUnitLabel(s.unit) || ''}</td>
        <td>${formatBrandMoney(s.price || 0)}</td>
        <td>${s.category || ''}</td>
        <td>${getStatusLabel(s.status)}</td>
      </tr>`;
    });
    
    htmlContent += `
    </tbody>
  </table>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `services_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportTXT = () => {
    const dataToExport = selectedRecords.length > 0 
      ? services.filter(s => selectedRecords.includes(s.id))
      : filteredServices;
    
    let txtContent = `${t('modules.article.pages.services.export.list_title_upper')}\n${t('modules.article.pages.services.export.date_label')} ${new Date().toLocaleDateString(dateLocale)}\n${t('modules.article.pages.services.export.total_label', { count: dataToExport.length })}\n\n`;
    txtContent += '='.repeat(100) + '\n\n';
    
    dataToExport.forEach(s => {
      txtContent += `${t('modules.article.pages.services.export.txt_fields.service_number')} ${s.service_number || ''}\n`;
      txtContent += `${t('modules.article.pages.services.export.txt_fields.name')} ${s.name || ''}\n`;
      txtContent += `${t('modules.article.pages.services.export.txt_fields.description')} ${s.description || ''}\n`;
      txtContent += `${t('modules.article.pages.services.export.txt_fields.unit')} ${resolveUnitLabel(s.unit) || ''}\n`;
      txtContent += `${t('modules.article.pages.services.export.txt_fields.price')} ${formatBrandMoney(s.price || 0)}\n`;
      txtContent += `${t('modules.article.pages.services.export.txt_fields.category')} ${s.category || ''}\n`;
      txtContent += `${t('modules.article.pages.services.export.txt_fields.status')} ${getStatusLabel(s.status)}\n`;
      txtContent += '-'.repeat(100) + '\n\n';
    });
    
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `services_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Import handlers
  const handleImportCSV = () => {
    const headers = exportHeaders();

    const schema = {
      requiredFields: ['name'],
      validators: {
        price: (value) => !value || !isNaN(parseFloat(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(t('modules.article.pages.services.alerts.import_errors', {
            validCount: result.validCount,
            invalidCount: result.invalidCount,
            errors: result.errors.join('\n'),
          }));
        } else {
          alert(t('modules.article.pages.services.alerts.import_success', { count: result.validCount }));
        }
        
        if (result.data.length > 0) {
          setServices([...services, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(t('modules.article.pages.services.alerts.import_file_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    const headers = exportHeaders();

    const schema = {
      requiredFields: ['name'],
      validators: {
        price: (value) => !value || !isNaN(parseFloat(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(t('modules.article.pages.services.alerts.import_errors', {
            validCount: result.validCount,
            invalidCount: result.invalidCount,
            errors: result.errors.join('\n'),
          }));
        } else {
          alert(t('modules.article.pages.services.alerts.import_success', { count: result.validCount }));
        }
        
        if (result.data.length > 0) {
          setServices([...services, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(t('modules.article.pages.services.alerts.import_file_error'));
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        
        if (!Array.isArray(data)) {
          alert(t('modules.article.pages.services.alerts.import_json_invalid'));
          return;
        }
        
        const importedServices = data.map((item, idx) => ({
          ...item,
          id: Date.now() + idx
        }));
        
        setServices([...services, ...importedServices]);
        alert(t('modules.article.pages.services.alerts.import_success', { count: importedServices.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('JSON import error:', error);
        alert(t('modules.article.pages.services.alerts.import_json_error'));
      }
    }, '.json');
  };

  const handleImportXML = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        
        const servicesNodes = xmlDoc.getElementsByTagName('service');
        const importedServices = [];
        
        for (let i = 0; i < servicesNodes.length; i++) {
          const node = servicesNodes[i];
          const service = {
            id: Date.now() + i,
            service_number: node.getElementsByTagName('service_number')[0]?.textContent || '',
            name: node.getElementsByTagName('name')[0]?.textContent || '',
            description: node.getElementsByTagName('description')[0]?.textContent || '',
            unit: node.getElementsByTagName('unit')[0]?.textContent || '',
            price: parseFloat(node.getElementsByTagName('price')[0]?.textContent || '0'),
            category: node.getElementsByTagName('category')[0]?.textContent || '',
            status: node.getElementsByTagName('status')[0]?.textContent || 'active'
          };
          importedServices.push(service);
        }
        
        setServices([...services, ...importedServices]);
        alert(t('modules.article.pages.services.alerts.import_success', { count: importedServices.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('XML import error:', error);
        alert(t('modules.article.pages.services.alerts.import_xml_error'));
      }
    }, '.xml');
  };

  const handleImportTXT = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const lines = content.split('\n').filter(line => line.trim());
        
        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        const headers = lines[0].split(delimiter).map(h => h.trim());
        
        const importedServices = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(delimiter);
          const service = { id: Date.now() + i };
          
          headers.forEach((header, index) => {
            service[header.toLowerCase().replace(/\s+/g, '_')] = values[index]?.trim() || '';
          });
          
          importedServices.push(service);
        }
        
        setServices([...services, ...importedServices]);
        alert(t('modules.article.pages.services.alerts.import_success', { count: importedServices.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('TXT import error:', error);
        alert(t('modules.article.pages.services.alerts.import_txt_error'));
      }
    }, '.txt,.tsv');
  };

  const generateServiceNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'service', 'SRV');
    return generateEntityReference(prefix, services, (s) => s.service_number || s.reference);
  }, [services, settings]);

  const openNewServiceModal = () => {
    const newNumber = articlesConfig.autoGenerateServiceReference !== false
      ? generateServiceNumber()
      : '';
    setFormData({ 
      service_number: newNumber, name_en: '', name_ar: '', name: '', description: '', category: '', unit: defaultServiceUnit || articlesConfig.defaultUnit || '', period: '',
      price: '', duration: '', status: 'active', taxRate: String(articlesConfig.defaultTaxRate ?? liveVatRate), notes: ''
    });
    setEditingRecord(null);
    setServiceFormTab('basic');
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ 
      service_number: '',
      name_en: '',
      name_ar: '',
      name: '',
      description: '',
      category: '',
      unit: defaultServiceUnit || '',
      period: '',
      price: '',
      duration: '',
      status: 'active',
      taxRate: liveVatRateStr,
      notes: ''
    });
    setEditingRecord(null);
    setServiceFormTab('basic');
    setShowModal(false);
  };

  
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredServices);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredServices.map((r) => r.id), [filteredServices]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const statusOptions = useMemo(() => buildServiceStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildServiceStatusColumnFilterOptions(t), [t]);

  const serviceStatusSelectOptions = useMemo(
    () => buildServiceStatusSelectOptions(t, { withIcons: true }),
    [t],
  );

  const orphanServiceUnits = useMemo(
    () => collectOrphanUnitValues(services, catalogServiceUnits),
    [services, catalogServiceUnits],
  );

  /** Unités depuis وحدات القياس + valeurs legacy non enregistrées. */
  const unitOptions = useMemo(
    () => buildUnitSelectOptionsFromRecords(catalogServiceUnits, {
      orphanValues: orphanServiceUnits,
      orphanSuffix: t('modules.article.pages.services.units.not_in_uom', { defaultValue: '(غير مسجّل في وحدات القياس)' }),
      resolveOrphanLabel: (value) => resolveCatalogUnitLabelArabic(value, t),
    }),
    [catalogServiceUnits, orphanServiceUnits, t],
  );

  const defaultServiceUnit = useMemo(
    () => defaultUnitFromOptions(unitOptions, ''),
    [unitOptions],
  );

  const unitColumnFilterOptions = useMemo(
    () => unitOptions.map(({ value, label }) => ({ value, label })),
    [unitOptions],
  );

  const resolveUnitLabel = useCallback(
    (unit) => resolveUnitLabelFromOptions(unit, unitOptions, t, {
      fallbackResolver: (value) => resolveCatalogUnitLabelArabic(value, t),
    }),
    [unitOptions, t],
  );

  const tvaOptions = useMemo(() => {
    const standardOption = {
      value: liveVatRateStr,
      label: qawaem
        ? `${liveVatRate}% (ض.ق.م)`
        : t('modules.article.pages.services.tva.normal'),
    };
    const options = [
      { value: '0', label: t('modules.article.pages.services.tva.exempt') },
    ];
    if (!qawaem) {
      options.push({ value: '9', label: t('modules.article.pages.services.tva.reduced') });
    }
    options.push(standardOption);
    options.push({ value: 'custom', label: t('modules.article.pages.services.tva.custom') });
    return options;
  }, [t, qawaem]);

  const periodOptions = useMemo(() => [
    { value: 'once', label: t('modules.article.pages.services.periods.once') },
    { value: 'daily', label: t('modules.article.pages.services.periods.daily') },
    { value: 'weekly', label: t('modules.article.pages.services.periods.weekly') },
    { value: 'monthly', label: t('modules.article.pages.services.periods.monthly') },
    { value: 'quarterly', label: t('modules.article.pages.services.periods.quarterly') },
    { value: 'semi_annual', label: t('modules.article.pages.services.periods.semi_annual') },
    { value: 'annual', label: t('modules.article.pages.services.periods.annual') },
    { value: 'project', label: t('modules.article.pages.services.periods.project') },
    { value: 'on_demand', label: t('modules.article.pages.services.periods.on_demand') },
  ], [t]);

  const durationPresetOptions = useMemo(() => [
    { value: '15m', label: t('modules.article.pages.services.durations.15m') },
    { value: '30m', label: t('modules.article.pages.services.durations.30m') },
    { value: '45m', label: t('modules.article.pages.services.durations.45m') },
    { value: '1h', label: t('modules.article.pages.services.durations.1h') },
    { value: '2h', label: t('modules.article.pages.services.durations.2h') },
    { value: '3h', label: t('modules.article.pages.services.durations.3h') },
    { value: '4h', label: t('modules.article.pages.services.durations.4h') },
    { value: 'half_day', label: t('modules.article.pages.services.durations.half_day') },
    { value: '1d', label: t('modules.article.pages.services.durations.1d') },
    { value: '2d', label: t('modules.article.pages.services.durations.2d') },
    { value: '3d', label: t('modules.article.pages.services.durations.3d') },
    { value: '5d', label: t('modules.article.pages.services.durations.5d') },
    { value: '1w', label: t('modules.article.pages.services.durations.1w') },
    { value: 'variable', label: t('modules.article.pages.services.durations.variable') },
  ], [t]);

  const resolvePeriodLabel = useCallback((period) => {
    const hit = periodOptions.find((o) => o.value === period);
    if (hit?.label) return hit.label;
    const legacyKey = `modules.article.pages.services.periods.${period}`;
    const legacy = t(legacyKey, { defaultValue: '' });
    if (legacy && legacy !== legacyKey) return legacy;
    return period || '';
  }, [periodOptions, t]);

  const resolveDurationLabel = useCallback((duration) => {
    const raw = String(duration ?? '').trim();
    if (!raw) return '';
    const legacyAliases = { '1j': '1d', '1s': '1w' };
    const normalized = legacyAliases[raw] ?? raw.toLowerCase();
    const hit = durationPresetOptions.find((o) => o.value === normalized || o.value === raw);
    if (hit?.label) return hit.label;
    const durationKey = `modules.article.pages.services.durations.${normalized}`;
    const fromDurations = t(durationKey, { defaultValue: '' });
    if (fromDurations && fromDurations !== durationKey) return fromDurations;
    const legacyKey = `modules.article.pages.services.periods.${normalized}`;
    const legacy = t(legacyKey, { defaultValue: '' });
    if (legacy && legacy !== legacyKey) return legacy;
    return raw;
  }, [durationPresetOptions, t]);

  const periodSelectOptions = useMemo(() => {
    const val = String(formData.period ?? '').trim();
    if (!val || periodOptions.some((o) => o.value === val)) return periodOptions;
    return [{ value: val, label: resolvePeriodLabel(val) }, ...periodOptions];
  }, [formData.period, periodOptions, resolvePeriodLabel]);

  const durationSelectOptions = useMemo(() => {
    const val = String(formData.duration ?? '').trim();
    if (!val || durationPresetOptions.some((o) => o.value === val)) return durationPresetOptions;
    return [{ value: val, label: resolveDurationLabel(val) }, ...durationPresetOptions];
  }, [formData.duration, durationPresetOptions, resolveDurationLabel]);

  const periodColumnFilterOptions = useMemo(
    () => periodOptions.map(({ value, label }) => ({ value, label })),
    [periodOptions],
  );

  // Simple non-clickable status badge
  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  // State for clickable status badge dropdown
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState(null);

  // Handle status change
  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/services/${recordId}`, { status: newStatus });
      setOpenStatusDropdown(null);
      setStatusDropdownPosition(null);
      loadServices();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Inline editing functions
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = services.find(s => s.id === recordId);
    if (!record) { cancelEditing(); return; }

    if (field === 'price' || field === 'taxRate') {
      const raw = String(value ?? '').replace(/\s/g, '').replace(/\u00a0/g, '').replace(',', '.');
      const n = parseFloat(raw);
      if (Number.isNaN(n)) { cancelEditing(); return; }
      const orig = parseFloat(record[field]);
      const origN = Number.isNaN(orig) ? 0 : orig;
      if (n === origN) { cancelEditing(); return; }
      try {
        await localApiAdapter.put(`/services/${recordId}`, { [field]: n });
        notifyUpdated(showNotify, entityLabel);
        await loadServices();
        cancelEditing();
        setViewingRecord((prev) => (prev && prev.id === recordId ? { ...prev, [field]: n } : prev));
      } catch (error) {
        console.error('Error saving inline edit:', error);
        notifyError(showNotify, t('modules.article.pages.services.alerts.save_error', { message: error.message || t('modules.article.pages.services.alerts.unknown_error') }));
        cancelEditing();
      }
      return;
    }

    const norm = (v) => String(v ?? '').trim();
    const newVal = norm(value);
    const originalVal = norm(record[field]);
    if (newVal === originalVal) {
      cancelEditing();
      return;
    }
    if (field === 'name' && !newVal) {
      cancelEditing();
      return;
    }

    const payload = { [field]: newVal };
    if (field === 'name_en' || field === 'name_ar') {
      const nextEn = field === 'name_en' ? newVal : norm(record.name_en);
      const nextAr = field === 'name_ar' ? newVal : norm(record.name_ar);
      payload.name_en = nextEn || nextAr || newVal;
      payload.name_ar = nextAr || null;
      payload.name = nextEn || nextAr || newVal;
    }

    try {
      await localApiAdapter.put(`/services/${recordId}`, field === 'name_en' || field === 'name_ar' ? payload : { [field]: newVal });
      notifyUpdated(showNotify, entityLabel);
      await loadServices();
      cancelEditing();
      setViewingRecord((prev) => {
        if (!prev || prev.id !== recordId) return prev;
        if (field === 'name_en' || field === 'name_ar') {
          return { ...prev, ...payload };
        }
        return { ...prev, [field]: newVal };
      });
    } catch (error) {
      console.error('Error saving inline edit:', error);
      notifyError(showNotify, t('modules.article.pages.services.alerts.save_error', { message: error.message || t('modules.article.pages.services.alerts.unknown_error') }));
      cancelEditing();
    }
  };

  // Clickable Status Badge Component
  const ClickableStatusBadge = ({ status, recordId }) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    const buttonRef = useRef(null);

    const handleToggleDropdown = (e) => {
      e.stopPropagation();
      if (openStatusDropdown === recordId) {
        setOpenStatusDropdown(null);
        setStatusDropdownPosition(null);
      } else {
        const rect = buttonRef.current.getBoundingClientRect();
        setStatusDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX + rect.width / 2
        });
        setOpenStatusDropdown(recordId);
      }
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (openStatusDropdown === recordId && buttonRef.current && !buttonRef.current.contains(event.target)) {
          const dropdown = document.querySelector(`[data-status-dropdown-for="${recordId}"]`);
          if (!dropdown || !dropdown.contains(event.target)) {
            setOpenStatusDropdown(null);
            setStatusDropdownPosition(null);
          }
        }
      };

      if (openStatusDropdown === recordId) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [openStatusDropdown, recordId]);

    return (
      <div className="relative inline-block">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggleDropdown}
          className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style} hover:opacity-80 transition-all cursor-pointer`}
          title={t('modules.article.pages.services.click_change_status')}
        >
          <Icon size={14} />{label}<ChevronDown size={12} className={`transition-transform ${openStatusDropdown === recordId ? 'rotate-180' : ''}`} />
        </button>
        {openStatusDropdown === recordId && statusDropdownPosition && ReactDOM.createPortal(
          <div 
            data-status-dropdown-for={recordId}
            className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden min-w-[140px] transform -translate-x-1/2" 
            style={{ position: 'fixed', zIndex: 99999, top: `${statusDropdownPosition.top}px`, left: `${statusDropdownPosition.left}px`, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
          >
            <div className="p-2">
              {statusOptions.map(option => {
                const OptionIcon = option.icon;
                return (
                  <button
                    key={option.value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleStatusChange(recordId, option.value);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left rounded-xl ${option.value === status ? 'bg-blue-500/25 shadow-inner' : ''}`}
                  >
                    <OptionIcon size={16} className={option.style.split(' ')[1].replace('text-', 'text-')} />
                    <span className="text-white text-sm font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || t('modules.article.pages.services.labels.no_category_fallback');
  };

  const qawaemStatusOptions = useMemo(
    () => [
      { value: 'all', label: t('modules.article.pages.services.all_statuses') },
      { value: 'active', label: t('modules.article.pages.services.status_values.active') },
      { value: 'inactive', label: t('modules.article.pages.services.status_values.inactive') },
    ],
    [t]
  );

  const qawaemCategoryOptions = useMemo(
    () => [
      { value: 'all', label: t('modules.article.pages.services.all_categories') },
      ...categories.map((cat) => ({ value: cat.id, label: cat.name })),
    ],
    [categories, t]
  );

  const qawaemStatusLabel =
    qawaemStatusOptions.find((o) => o.value === statusFilter)?.label ??
    t('modules.article.pages.services.all_statuses');

  const qawaemCategoryLabel =
    categoryFilter === 'all'
      ? t('modules.article.pages.services.all_categories')
      : getCategoryName(categoryFilter);

  const qawaemBaseColumns = useMemo(
    () => [
      { key: 'service_number', label: t('modules.article.pages.services.columns.service_number'), required: true },
      { key: 'name', label: t('modules.article.pages.services.columns.service_name'), required: true },
      { key: 'category', label: t('modules.article.pages.services.columns.category') },
      { key: 'unit', label: t('modules.article.pages.services.columns.unit') },
      { key: 'price', label: t('modules.article.pages.services.columns.price') },
      { key: 'duration', label: t('modules.article.pages.services.columns.duration') },
      { key: 'period', label: t('modules.article.pages.services.columns.period') },
      { key: 'tax_rate', label: t('modules.article.pages.services.columns.tax_rate') },
      { key: 'status', label: t('modules.article.pages.services.columns.status') },
    ],
    [t],
  );
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemServiceColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('modules.article.pages.services.columns.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );

  const listStats = useMemo(() => [
    { key: 'total', title: t('modules.article.pages.services.stats.total'), value: stats.total, icon: Briefcase, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: t('modules.article.pages.services.stats.active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'inactive', title: t('modules.article.pages.services.stats.inactive'), value: stats.inactive, icon: XCircle, color: 'from-red-500 to-red-600' },
    { key: 'categories', title: t('modules.article.pages.services.stats.categories'), value: stats.categories, icon: Tag, color: 'from-amber-500 to-amber-600' },
    { key: 'revenue', title: t('modules.article.pages.services.stats.total_revenue'), value: formatBrandMoney(stats.totalRevenue), icon: DollarSign, color: 'from-purple-500 to-purple-600', className: 'max-lg:hidden' },
  ], [stats, t]);

  const renderImportExportActions = (compact = false) => (
    <>
      <div className="relative" style={{ zIndex: 100001 }} ref={importDropdownRef}>
        <button
          type="button"
          onClick={() => {
            setShowImportDropdown(!showImportDropdown);
            setShowExportDropdown(false);
          }}
          title={t('modules.article.pages.services.import_aria')}
          aria-label={t('modules.article.pages.services.import_aria')}
          className={
            qawaem
              ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold shrink-0 border border-[#a67c3d]/45 bg-gradient-to-b from-[#faf8f4] to-[#f9f3ea] text-[#704820] hover:border-[#8a6530]/55'
              : 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5'
          }
        >
          <Upload size={16} className="shrink-0" />
          {!compact ? <span className="hidden sm:inline text-sm font-medium">{t('modules.article.pages.services.import')}</span> : null}
          <ChevronDown size={14} className={`shrink-0 transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
        </button>
        {showImportDropdown && (
          <div
            className={`qawaem-portal-dropdown border rounded-2xl shadow-2xl min-w-[140px] ${qawaem ? '' : 'border-blue-400/60'}`}
            style={
              qawaem
                ? { position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002 }
                : {
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    zIndex: 100002,
                    overflow: 'visible',
                    backgroundImage:
                      'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                  }
            }
          >
            <div className="p-2">
              <button type="button" onClick={handleImportCSV} className="w-full px-4 py-2.5 text-start transition-colors flex items-center gap-3 rounded-xl hover:bg-[#6e9b6e]/10 text-sm font-medium text-forest-900"><FileText size={16} className="text-red-400" /><span>{t('common.format_csv')}</span></button>
              <button type="button" onClick={handleImportExcel} className="w-full px-4 py-2.5 text-start transition-colors flex items-center gap-3 rounded-xl hover:bg-[#6e9b6e]/10 text-sm font-medium text-forest-900"><FileSpreadsheet size={16} className="text-green-500" /><span>{t('common.format_excel')}</span></button>
              <button type="button" onClick={handleImportJSON} className="w-full px-4 py-2.5 text-start transition-colors flex items-center gap-3 rounded-xl hover:bg-[#6e9b6e]/10 text-sm font-medium text-forest-900"><FileText size={16} className="text-yellow-500" /><span>JSON</span></button>
              <button type="button" onClick={handleImportXML} className="w-full px-4 py-2.5 text-start transition-colors flex items-center gap-3 rounded-xl hover:bg-[#6e9b6e]/10 text-sm font-medium text-forest-900"><FileText size={16} className="text-orange-400" /><span>XML</span></button>
              <button type="button" onClick={handleImportTXT} className="w-full px-4 py-2.5 text-start transition-colors flex items-center gap-3 rounded-xl hover:bg-[#6e9b6e]/10 text-sm font-medium text-forest-900"><FileText size={16} className="text-gray-400" /><span>TXT/TSV</span></button>
            </div>
          </div>
        )}
      </div>
      <div className="relative" style={{ zIndex: 100001 }} ref={exportDropdownRef}>
        <button
          type="button"
          onClick={() => {
            setShowExportDropdown(!showExportDropdown);
            setShowImportDropdown(false);
          }}
          title={t('modules.article.pages.services.export_aria')}
          aria-label={t('modules.article.pages.services.export_aria')}
          className={
            qawaem
              ? 'toolbar-export-trigger inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold shrink-0'
              : 'bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5'
          }
        >
          <Download size={16} className="shrink-0" />
          {!compact ? <span className="hidden sm:inline text-sm font-medium">{t('common.export')}</span> : null}
          <ChevronDown size={14} className={`shrink-0 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
        </button>
        {showExportDropdown && (
          <div
            className={`qawaem-portal-dropdown ${qawaem ? 'toolbar-export-panel' : 'border border-blue-400/60 rounded-2xl shadow-2xl min-w-[140px]'}`}
            style={
              qawaem
                ? { position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, minWidth: '180px' }
                : {
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    zIndex: 100002,
                    overflow: 'visible',
                    backgroundImage:
                      'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                  }
            }
          >
            {qawaem ? (
              <>
                <div className="toolbar-export-panel-head">
                  <span>تصدير ذكي</span>
                </div>
                <ul className="p-1.5 space-y-0.5">
                  <li><button type="button" onClick={handleExportPDF} disabled={isGeneratingPDF} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start disabled:opacity-50"><span className="toolbar-export-item-icon text-rose-500"><FileText className="h-4 w-4" /></span><span className="text-sm font-medium text-forest-900">{isGeneratingPDF ? t('modules.article.pages.services.generating') : t('common.format_pdf')}</span></button></li>
                  <li><button type="button" onClick={handleExportExcel} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start"><span className="toolbar-export-item-icon text-emerald-500"><FileSpreadsheet className="h-4 w-4" /></span><span className="text-sm font-medium text-forest-900">{t('common.format_excel')}</span></button></li>
                  <li><button type="button" onClick={handleExportCSV} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start"><span className="toolbar-export-item-icon text-violet-500"><FileOutput className="h-4 w-4" /></span><span className="text-sm font-medium text-forest-900">{t('common.format_csv')}</span></button></li>
                  <li><button type="button" onClick={handleExportJSON} className="toolbar-export-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-start"><span className="toolbar-export-item-icon text-yellow-500"><FileText className="h-4 w-4" /></span><span className="text-sm font-medium text-forest-900">JSON</span></button></li>
                </ul>
              </>
            ) : (
              <div className="p-2">
                <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? t('modules.article.pages.services.generating') : t('common.format_pdf')}</span></button>
                <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-pink-400" /><span className="text-sm font-medium">HTML</span></button>
                <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  const serviceFormTabs = useMemo(() => ([
    { id: 'basic', label: t('modules.article.pages.services.sections.basic_info'), icon: Briefcase, iconColor: 'text-blue-400' },
    { id: 'pricing', label: t('modules.article.pages.services.sections.pricing_notes', { defaultValue: 'التسعير والملاحظات' }), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [t]);

  return (
    <>
      <FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={t('modules.article.pages.services.title')}
      subtitle={t('modules.article.pages.services.subtitle')}
      icon={Briefcase}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={t('modules.article.pages.services.search_placeholder')}
      onCreate={openNewServiceModal}
      createLabel={t('modules.article.pages.services.new_service')}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: t('modules.article.pages.services.import'),
        exportLabel: t('common.export'),
        generatingLabel: t('modules.article.pages.services.generating'),
        isGeneratingPDF: isGeneratingPDF,
        onImportCSV: handleImportCSV,
        onImportExcel: handleImportExcel,
        onImportJSON: handleImportJSON,
        onImportXML: handleImportXML,
        onImportTXT: handleImportTXT,
        onExportPDF: handleExportPDF,
        onExportExcel: handleExportExcel,
        onExportCSV: handleExportCSV,
        onExportJSON: handleExportJSON,
        onExportXML: handleExportXML,
        onExportHTML: handleExportHTML,
        onExportTXT: handleExportTXT,
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={t('modules.article.pages.services.advanced_options')}
      advancedOptionsContent={(
<div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_services' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {t('modules.article.pages.services.distribution.reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
              {distributionInfo.data.map((item) => (
                <button
                  key={item.filterId || item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterId ? null : item.filterId)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterId 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={t('modules.article.pages.services.distribution.click_filter', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterId ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterId ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    const percent = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    return t('modules.article.pages.services.distribution.percent_of_services', { percent });
                  })()}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {shouldShowCardsToolbarSearch(viewMode) ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={t('modules.article.pages.services.search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              
              {/* View Mode Toggle - FIRST ELEMENT */}
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
                cardsTitle={t('modules.article.pages.services.view_cards')}
                tableTitle={t('modules.article.pages.services.view_table')}
              />
              
              {/* Category Filter Dropdown */}
              <div className="relative" ref={categoryFilterRef}>
                <button 
                  type="button"
                  onClick={() => setIsCategoryFilterOpen(!isCategoryFilterOpen)} 
                  title={categoryFilter === 'all' ? t('modules.article.pages.services.filter_by_category') : t('modules.article.pages.services.category_label', { name: getCategoryName(categoryFilter) })}
                  aria-label={categoryFilter === 'all' ? t('modules.article.pages.services.filter_by_category') : t('modules.article.pages.services.category_label', { name: getCategoryName(categoryFilter) })}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Tag size={18} className="text-white/80 shrink-0" />
                  <span className="hidden sm:inline text-white font-medium text-sm">
                    {categoryFilter === 'all' ? t('modules.article.pages.services.category') : getCategoryName(categoryFilter)}
                  </span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 text-white/70 transition-transform ${isCategoryFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isCategoryFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setCategoryFilter('all');
                          setIsCategoryFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${categoryFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-white/50"></div>
                        <span className="text-sm font-medium">{t('modules.article.pages.services.all_categories')}</span>
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setCategoryFilter(cat.id);
                            setIsCategoryFilterOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${categoryFilter === cat.id ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                        >
                          <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                          <span className="text-sm font-medium">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Filter Dropdown */}
              <div className="relative" ref={statusFilterRef}>
                <button 
                  type="button"
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} 
                  title={statusFilter === 'all' ? t('modules.article.pages.services.filter_by_status') : statusFilter === 'active' ? t('modules.article.pages.services.status_active_label') : t('modules.article.pages.services.status_inactive_label')}
                  aria-label={statusFilter === 'all' ? t('modules.article.pages.services.filter_by_status') : statusFilter === 'active' ? t('modules.article.pages.services.status_active_label') : t('modules.article.pages.services.status_inactive_label')}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80 shrink-0" />
                  <span className="hidden sm:inline text-white font-medium text-sm">
                    {statusFilter === 'all' ? t('modules.article.pages.services.status') : statusFilter === 'active' ? t('modules.article.pages.services.status_values.active') : t('modules.article.pages.services.status_values.inactive')}
                  </span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-white/50"></div>
                        <span className="text-sm font-medium">{t('modules.article.pages.services.all_statuses')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('active');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'active' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-sm font-medium">{t('modules.article.pages.services.status_values.active')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('inactive');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'inactive' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-red-400"></div>
                        <span className="text-sm font-medium">{t('modules.article.pages.services.status_values.inactive')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedRecords.length > 0 && (
                <button 
                  onClick={openBulkDeleteServices}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium"
                  title={t('modules.article.pages.services.delete_selection')}
                >
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">{st('delete_with_count', { count: selectedRecords.length })}</span>
                </button>
              )}
              
              {/* Import Dropdown - RED */}
              <div className="relative" style={{ zIndex: 100001 }} ref={importDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowImportDropdown(!showImportDropdown);
                    setShowExportDropdown(false);
                  }}
                  title={t('modules.article.pages.services.import_aria')}
                  aria-label={t('modules.article.pages.services.import_aria')}
                  className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Upload size={16} className="shrink-0" />
                  <span className="hidden sm:inline text-sm font-medium">{t('modules.article.pages.services.import')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, overflow: 'visible', backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
                  >
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={handleImportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={handleImportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">TXT/TSV</span></button>
                    </div>
                  </div>
                )}
              </div>

              {/* Export Dropdown - GREEN */}
              <div className="relative" style={{ zIndex: 100001 }} ref={exportDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowExportDropdown(!showExportDropdown);
                    setShowImportDropdown(false);
                  }}
                  title={t('modules.article.pages.services.export_aria')}
                  aria-label={t('modules.article.pages.services.export_aria')}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Download size={16} className="shrink-0" />
                  <span className="hidden sm:inline text-sm font-medium">{t('common.export')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 100002,
                      overflow: 'visible',
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? t('modules.article.pages.services.generating') : t('common.format_pdf')}</span></button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-pink-400" /><span className="text-sm font-medium">HTML</span></button>
                      <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={openBulkDeleteServices}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{st('delete_with_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
        {/* Table View */}
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>

                  {showCol('service_number') ? <TableColumnHeaderFilter columnKey="service_number" label={t('modules.article.pages.services.columns.service_number')} value={colFilters.service_number ?? ''} onChange={(v) => setColFilter('service_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('name') ? <TableColumnHeaderFilter columnKey="name" label={t('modules.article.pages.services.columns.service_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('category') ? <TableColumnHeaderFilter columnKey="category" label={t('modules.article.pages.services.columns.category')} value={colFilters.category ?? ''} onChange={(v) => setColFilter('category', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('unit') ? <TableColumnHeaderFilter columnKey="unit" label={t('modules.article.pages.services.columns.unit')} kind="select" options={unitColumnFilterOptions} value={colFilters.unit ?? ''} onChange={(v) => setColFilter('unit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('price') ? <TableColumnHeaderFilter columnKey="price" label={t('modules.article.pages.services.columns.price')} value={colFilters.price ?? ''} onChange={(v) => setColFilter('price', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('duration') ? <TableColumnHeaderFilter columnKey="duration" label={t('modules.article.pages.services.columns.duration')} value={colFilters.duration ?? ''} onChange={(v) => setColFilter('duration', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('period') ? <TableColumnHeaderFilter columnKey="period" label={t('modules.article.pages.services.columns.period')} kind="select" options={periodColumnFilterOptions} value={colFilters.period ?? ''} onChange={(v) => setColFilter('period', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  {showCol('tax_rate') ? <TableColumnHeaderFilter columnKey="tax_rate" label={t('modules.article.pages.services.columns.tax_rate')} value={colFilters.tax_rate ?? ''} onChange={(v) => setColFilter('tax_rate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('modules.article.pages.services.columns.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={t('modules.article.pages.services.columns.actions')}
                    columns={qawaemServiceColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemServiceColumns.filter((c) => c.checked).length}
                    totalCount={qawaemServiceColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredServices.length > 0 ? (
                  pagedItems.map((record, index) => {
                    return (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>

                        {showCol('service_number') ? (
                          <td className="px-6 py-4">
                            <ServiceEditableCell recordId={record.id} field="service_number" value={record.service_number} className="text-white font-semibold"
                              isEditing={editingCell?.recordId === record.id && editingCell?.field === 'service_number'}
                              editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </td>
                        ) : null}
                        {showCol('name') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <ServiceEditableCell recordId={record.id} field="name" value={record.name} className="text-white font-semibold"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                              <ServiceEditableCell recordId={record.id} field="description" value={record.description} className="text-white/50 text-xs mt-0.5"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'description'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                          </td>
                        ) : null}
                        {showCol('category') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="text-white font-semibold">{getCategoryName(record.category)}</span>
                              <span className="text-white/50 text-xs mt-0.5">{t('modules.article.pages.services.hints.service_category')}</span>
                            </div>
                          </td>
                        ) : null}
                        {showCol('unit') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <ServiceEditableCell recordId={record.id} field="unit" value={record.unit || ''} displayValue={resolveUnitLabel(record.unit)}
                                className="text-white font-semibold"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'unit'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                              <span className="text-white/50 text-xs mt-0.5">{t('modules.article.pages.services.hints.unit_measure')}</span>
                            </div>
                          </td>
                        ) : null}
                        {showCol('price') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <ServiceEditableCell recordId={record.id} field="price" value={record.price != null && record.price !== '' ? String(record.price) : ''} className="text-white font-semibold"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'price'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                              <span className="text-white/40 text-xs mt-0.5">{t('modules.article.pages.services.hints.unit_price')}</span>
                            </div>
                          </td>
                        ) : null}
                        {showCol('duration') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <ServiceEditableCell recordId={record.id} field="duration" value={record.duration || ''} displayValue={resolveDurationLabel(record.duration)} className="text-white font-semibold"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'duration'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                              <span className="text-white/50 text-xs mt-0.5">{t('modules.article.pages.services.hints.estimated_duration')}</span>
                            </div>
                          </td>
                        ) : null}
                        {showCol('period') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <ServiceEditableCell recordId={record.id} field="period" value={record.period || ''} displayValue={resolvePeriodLabel(record.period)}
                                className="text-white font-semibold"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'period'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                              <span className="text-white/50 text-xs mt-0.5">{t('modules.article.pages.services.hints.period_billing')}</span>
                            </div>
                          </td>
                        ) : null}
                        {showCol('tax_rate') ? (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="text-white font-semibold">
                                <ServiceEditableCell recordId={record.id} field="taxRate" value={record.taxRate != null && record.taxRate !== '' ? String(record.taxRate) : ''} className="text-white font-semibold inline"
                                  isEditing={editingCell?.recordId === record.id && editingCell?.field === 'taxRate'}
                                  editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                                />%
                              </span>
                              <span className="text-white/50 text-xs mt-0.5">{t('modules.article.pages.services.hints.tax_rate')}</span>
                            </div>
                          </td>
                        ) : null}
                        {showCol('status') ? (
                          <td className="px-6 py-4 text-center"><div className="flex justify-center"><ClickableStatusBadge status={record.status} recordId={record.id} /></div></td>
                        ) : null}
                        <QawaemTableStickyActionsTd>
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => openDeleteService(record.id)}
                            viewTitle={t('modules.article.pages.services.actions.view')}
                            editTitle={t('modules.article.pages.services.actions.edit')}
                            deleteTitle={t('modules.article.pages.services.actions.delete')}
                          />
                        </QawaemTableStickyActionsTd>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><Briefcase size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{t('modules.article.pages.services.empty.no_services')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{t('modules.article.pages.services.footer.services_total', { count: filteredServices.length })}</span>
                      <span>{t('modules.article.pages.services.footer.active_count', { count: servicesFooter.actifs })}</span>
                      <span>{t('modules.article.pages.services.footer.catalog_valuation', { amount: formatBrandMoney(servicesFooter.prixCatalogue, false) })}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Card View — two cards per row on large screens */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredServices.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-5 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 min-w-0">
                          <Briefcase size={20} className="text-blue-400 shrink-0" />
                          <ServiceEditableCell recordId={record.id} field="name" value={record.name} className="text-xl font-bold text-white break-words"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                        </h3>
                        <div className="flex"><ClickableStatusBadge status={record.status} recordId={record.id} /></div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Informations de Base */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{t('modules.article.pages.services.sections.basic_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.description_colon')}</span>{' '}
                              <ServiceEditableCell recordId={record.id} field="description" value={record.description} className="text-white/85 inline text-sm"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'description'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.category_colon')}</span> {getCategoryName(record.category)}</div>
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.unit_colon')}</span>{' '}
                              <ServiceEditableCell recordId={record.id} field="unit" value={record.unit || ''} displayValue={resolveUnitLabel(record.unit)} className="text-white/90 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'unit'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Tarification */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{t('modules.article.pages.services.sections.pricing')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2 flex-wrap">
                              <DollarSign size={16} className="text-green-400 shrink-0" />
                              <span className="font-medium">{t('modules.article.pages.services.labels.price_colon')}</span>{' '}
                              <ServiceEditableCell recordId={record.id} field="price" value={record.price != null && record.price !== '' ? String(record.price) : ''} className="text-white inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'price'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              /> {getBrandCurrencySuffix()}
                            </div>
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.tax_colon')}</span>{' '}
                              <ServiceEditableCell recordId={record.id} field="taxRate" value={record.taxRate != null && record.taxRate !== '' ? String(record.taxRate) : ''} className="text-white/90 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'taxRate'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />%
                            </div>
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.price_ttc_colon')}</span> {(() => {
                              const price = parseFloat(record.price) || 0;
                              const taxRate = parseFloat(record.taxRate);
                              const finalTaxRate = isNaN(taxRate) ? liveVatRate : taxRate;
                              return (price * (1 + finalTaxRate / 100)).toFixed(2);
                            })()} {getBrandCurrencySuffix()}</div>
                          </div>
                        </div>
                        
                        {/* Détails du Service */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{t('modules.article.pages.services.sections.service_details')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.duration_colon')}</span>{' '}
                              <ServiceEditableCell recordId={record.id} field="duration" value={record.duration || ''} displayValue={resolveDurationLabel(record.duration)} className="text-white/90 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'duration'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                            <div><span className="font-medium">{t('modules.article.pages.services.labels.period_colon')}</span>{' '}
                              <ServiceEditableCell recordId={record.id} field="period" value={record.period || ''} displayValue={resolvePeriodLabel(record.period)} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'period'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Notes */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{t('modules.article.pages.services.sections.notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p className="text-white/80 text-xs">
                              <ServiceEditableCell recordId={record.id} field="notes" value={record.notes} className="text-white/80 text-xs"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'notes'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 shrink-0 sm:ms-2">
                      <QawaemErpRowActions
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => openDeleteService(record.id)}
                        viewTitle={t('modules.article.pages.services.actions.view')}
                        editTitle={t('modules.article.pages.services.actions.edit')}
                        deleteTitle={t('modules.article.pages.services.actions.delete')}
                        compact
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Briefcase size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{t('modules.article.pages.services.empty.no_services')}</p>
              </div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {/* View Modal - Shows 100% of data - WIDE LAYOUT */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className={`backdrop-blur-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`}
              style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '95%', maxWidth: '1100px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{t('modules.article.pages.services.modal.view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Basic Info */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase size={18} className="text-blue-400" />
                    {t('modules.article.pages.services.sections.basic_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.name_en')}</p>
                      <div className="text-white font-medium text-lg"><ServiceEditableCell recordId={viewingRecord.id} field="name_en" value={viewingRecord.name_en || viewingRecord.name} className="text-white font-medium text-lg"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'name_en'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.name_ar')}</p>
                      <div className="text-white font-medium text-lg" dir="rtl"><ServiceEditableCell recordId={viewingRecord.id} field="name_ar" value={viewingRecord.name_ar || ''} className="text-white font-medium text-lg"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'name_ar'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.category')}</p>
                      <p className="text-white font-medium">{getCategoryName(viewingRecord.category)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.unit')}</p>
                      <div className="text-white font-medium"><ServiceEditableCell recordId={viewingRecord.id} field="unit" value={viewingRecord.unit || ''} displayValue={resolveUnitLabel(viewingRecord.unit)} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'unit'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.status')}</p>
                      <div className="flex">{getStatusBadge(viewingRecord.status)}</div>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.description')}</p>
                      <div className="text-white font-medium"><ServiceEditableCell recordId={viewingRecord.id} field="description" value={viewingRecord.description} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'description'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                    </div>
                  </div>
                </div>

                {/* Pricing & Duration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-green-400" />
                      {t('modules.article.pages.services.sections.pricing')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.labels.unit_price')}</p>
                        <div className="text-white font-medium text-lg"><ServiceEditableCell recordId={viewingRecord.id} field="price" value={viewingRecord.price != null && viewingRecord.price !== '' ? String(viewingRecord.price) : ''} className="text-white font-medium text-lg"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'price'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /> {getBrandCurrencySuffix()}</div>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.tax_rate')}</p>
                        <div className="text-white font-medium"><ServiceEditableCell recordId={viewingRecord.id} field="taxRate" value={viewingRecord.taxRate != null && viewingRecord.taxRate !== '' ? String(viewingRecord.taxRate) : ''} className="text-white font-medium inline"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'taxRate'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        />%</div>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.labels.price_ttc')}</p>
                        <p className="text-white font-medium">{(() => {
                          const price = parseFloat(viewingRecord.price) || 0;
                          const taxRate = parseFloat(viewingRecord.taxRate);
                          const finalTaxRate = isNaN(taxRate) ? liveVatRate : taxRate;
                          return (price * (1 + finalTaxRate / 100)).toFixed(2);
                        })()} {getBrandCurrencySuffix()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Clock size={18} className="text-blue-400" />
                      {t('modules.article.pages.services.sections.service_details')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.duration')}</p>
                        <div className="text-white font-medium"><ServiceEditableCell recordId={viewingRecord.id} field="duration" value={viewingRecord.duration || ''} displayValue={resolveDurationLabel(viewingRecord.duration)} className="text-white font-medium"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'duration'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /></div>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{t('modules.article.pages.services.columns.period')}</p>
                        <div className="text-white font-medium"><ServiceEditableCell recordId={viewingRecord.id} field="period" value={viewingRecord.period || ''} displayValue={resolvePeriodLabel(viewingRecord.period)} className="text-white font-medium"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'period'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-purple-400" />
                      {t('modules.article.pages.services.sections.notes')}
                    </h3>
                    <div className="text-white/80"><ServiceEditableCell recordId={viewingRecord.id} field="notes" value={viewingRecord.notes} className="text-white/80"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'notes'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                    /></div>
                  </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div ref={serviceModalPanelRef} data-service-modal-panel="true" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? t('modules.article.pages.services.modal.edit_title') : t('modules.article.pages.services.modal.create_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="service-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? t('modules.article.pages.services.modal.update') : t('modules.article.pages.services.modal.create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            
            <FacturationFormModalTabs
              tabs={serviceFormTabs}
              activeTab={serviceFormTab}
              onTabChange={setServiceFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? t('modules.article.pages.services.modal.edit_title') : t('modules.article.pages.services.modal.create_title')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="service-form" className={ERP_MODAL_FORM_CLASS} onSubmit={handleSubmit}>
                {serviceFormTab === 'basic' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={t('modules.article.pages.services.labels.service_number_required')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.service_number}
                            onChange={(e) => setFormData({ ...formData, service_number: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                            placeholder={t('modules.article.pages.services.placeholders.service_number')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={t('modules.article.pages.services.columns.name_en')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.name_en}
                            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            placeholder={t('modules.article.pages.services.placeholders.name_en')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={t('modules.article.pages.services.columns.name_ar')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.name_ar}
                            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            placeholder={t('modules.article.pages.services.placeholders.name_ar')}
                            dir="rtl"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.columns.category')}</label>
                          <CustomSelect
                            value={formData.category === '' || formData.category == null ? '' : formData.category}
                            onChange={(value) =>
                              setFormData({
                                ...formData,
                                category: value === '__clear__' || value === '' || value == null ? '' : value,
                              })
                            }
                            options={serviceCategorySelectOptions}
                            placeholder={t('modules.article.pages.services.placeholders.select_category')}
                            searchable
                            creatable={false}
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={t('modules.article.pages.services.labels.unit_required')} required />
                          </label>
                          <CustomSelect
                            value={formData.unit || ''}
                            onChange={(value) => setFormData({ ...formData, unit: value || '' })}
                            options={unitOptions}
                            placeholder={t('modules.article.pages.services.placeholders.select_unit')}
                            searchable
                            creatable={false}
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.columns.status')}</label>
                          <CustomSelect
                            value={formData.status}
                            onChange={(value) => setFormData({ ...formData, status: value })}
                            options={serviceStatusSelectOptions}
                            placeholder={t('modules.article.pages.services.placeholders.select_status')}
                            searchable={false}
                            creatable={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.columns.duration')}</label>
                          <CustomSelect
                            value={formData.duration || ''}
                            onChange={(value) => setFormData({ ...formData, duration: value || '' })}
                            options={durationSelectOptions}
                            placeholder={t('modules.article.pages.services.placeholders.duration')}
                            searchable
                            creatable
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.columns.period')}</label>
                          <CustomSelect
                            value={formData.period || ''}
                            onChange={(value) => setFormData({ ...formData, period: value || '' })}
                            options={periodSelectOptions}
                            placeholder={t('modules.article.pages.services.placeholders.period')}
                            searchable
                            creatable
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0 md:col-span-3">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.columns.description')}</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full min-h-[9.5rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm resize-none"
                            placeholder={t('modules.article.pages.services.placeholders.description')}
                            rows={4}
                          />
                        </div>
                      </div>
                )}

                {serviceFormTab === 'pricing' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t(qawaem ? 'modules.article.pages.services.labels.unit_price_sar' : 'modules.article.pages.services.labels.unit_price_da')}</label>
                          <CustomNumberInput
                            value={formData.price}
                            onChange={(value) => setFormData({ ...formData, price: value })}
                            placeholder={t('modules.article.pages.services.placeholders.price')}
                            step={0.01}
                            decimals={2}
                            min={0}
                          />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.labels.tax_percent')}</label>
                          <div className="relative" ref={tvaDropdownRef}>
                            <button
                              type="button"
                              data-services-tva-dropdown-button="true"
                              onClick={() => setShowTVADropdown(!showTVADropdown)}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between"
                            >
                              <span>
                                {formData.taxRate === '0' || formData.taxRate === liveVatRateStr || (!qawaem && formData.taxRate === '9') 
                                  ? tvaOptions.find(opt => opt.value === formData.taxRate)?.label 
                                  : t('modules.article.pages.services.labels.custom_vat_display', { rate: formData.taxRate })}
                              </span>
                              <ChevronDown size={16} className={`transition-transform ${showTVADropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showTVADropdown && ReactDOM.createPortal(
                              <div 
                                data-services-tva-dropdown="true"
                                className={`rounded-2xl shadow-2xl overflow-hidden ${isDashboardLightMode ? 'border border-gray-200' : 'border border-blue-400/60'}`}
                                style={{ 
                                  position: 'fixed',
                                  ...(isDashboardLightMode
                                    ? {
                                        background: '#ffffff',
                                        boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12)',
                                        border: '1px solid rgba(15, 23, 42, 0.12)',
                                      }
                                    : {
                                        backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                                        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                                      }),
                                  ...(tvaDropdownRef.current && (() => {
                                    const rect = tvaDropdownRef.current.getBoundingClientRect();
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    const spaceAbove = rect.top;
                                    const dropdownHeight = 220;
                                    
                                    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                                      return { bottom: window.innerHeight - rect.top + 8, left: rect.left, width: rect.width };
                                    } else {
                                      return { top: rect.bottom + 8, left: rect.left, width: rect.width };
                                    }
                                  })()),
                                  zIndex: 99999,
                                }}
                              >
                                {tvaOptions.map(option => (
                                  <button 
                                    key={option.value} 
                                    type="button" 
                                    onClick={() => { 
                                      if (option.value === 'custom') {
                                        // Show custom input prompt
                                        const customValue = prompt(t('modules.article.pages.services.placeholders.custom_vat_prompt'), formData.taxRate || liveVatRateStr);
                                        if (customValue !== null && !isNaN(customValue) && customValue >= 0 && customValue <= 100) {
                                          setFormData({ ...formData, taxRate: customValue });
                                        }
                                      } else {
                                        setFormData({ ...formData, taxRate: option.value });
                                      }
                                      setShowTVADropdown(false);
                                    }} 
                                    className={`w-full flex items-center px-4 py-2.5 transition-all text-left text-sm font-medium ${isDashboardLightMode ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-blue-500/20'}`}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>,
                              document.body
                            )}
                          </div>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 flex flex-col justify-center">
                          <p className="text-white/80 text-sm">
                            <span className="font-medium">{t('modules.article.pages.services.labels.price_ttc')}</span>{' '}
                            <span className="text-white font-bold">
                              {(() => {
                                const price = parseFloat(formData.price) || 0;
                                const taxRate = parseFloat(formData.taxRate);
                                const finalTaxRate = isNaN(taxRate) ? liveVatRate : taxRate;
                                return (price * (1 + finalTaxRate / 100)).toFixed(2);
                              })()} {getBrandCurrencySuffix()}
                            </span>
                          </p>
                        </div>
                        <div className="min-w-0 md:col-span-3">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{t('modules.article.pages.services.labels.additional_notes')}</label>
                          <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm resize-none"
                            placeholder={t('modules.article.pages.services.placeholders.notes')}
                            rows={2}
                          />
                        </div>
                      
                      </div>
                )}

              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppConfirmDialog
        open={deleteConfirm.open}
        title={
          deleteConfirm.ids.length > 1
            ? t('common.delete', 'حذف')
            : `${t('common.delete', 'حذف')} — ${entityLabel}`
        }
        message={
          deleteConfirm.ids.length > 1
            ? st('alerts.bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : st('alerts.delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteServices}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default Services;

